import { Badge } from '@/components/common/badge'
import { Button } from '@/components/common/button'
import { Note } from '@/components/common/note'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/common/table'
import { TabsContent } from '@/components/common/tabs'
import { useConnectWalletModalContext } from '@/components/connect/connectModal'
import { TxSteps } from '@/components/opportunity/sidePanel/txSteps'
import { useTransaction } from '@/hooks/useTransaction'
import { useActionsQuery } from '@/queries/actions.query'
import type { TransactionsContext } from '@/queries/transactionsContext.query'
import type { StepData } from '@/types/transactionsContext'
import type { Vault } from '@/types/vault'
import { getProtocolDisplayName } from '@/utils/protocol'
import { formatPreciseTokenValue } from '@vaultsfyi/common/utils'
import { useMemo, useState } from 'react'
import type { Address } from 'viem'
import { z } from 'zod'

const ClaimStatus = {
  READY: 'ready',
  PENDING: 'pending',
} as const

const claimDataSchema = z.array(
  z.object({
    id: z.string(),
    assetAmount: z.string(),
    lpTokenAmount: z.string(),
    status: z.nativeEnum(ClaimStatus),
  })
)

interface Props {
  account: Address | undefined
  vault: Vault
  stepData: StepData
  transactionsContext: TransactionsContext
  setTransactionFinished: ({ txHash, amount }: { txHash: string | undefined; amount: string }) => void
  txState: 'idle' | 'loading' | 'success'
}

export function EtherfiClaimWithdrawTab({
  account,
  vault,
  stepData,
  transactionsContext,
  setTransactionFinished,
  txState,
}: Props) {
  const { setOpenModal: setOpenWalletModal } = useConnectWalletModalContext()
  const { asset, network, protocol } = vault
  const protocolDisplayName = getProtocolDisplayName(protocol)

  const claimData = useMemo(() => {
    return claimDataSchema.parse((transactionsContext?.vaultSpecificData as Record<string, any>)['claimData'])
  }, [transactionsContext])

  const { data: actions, isFetching } = useActionsQuery({
    address: account,
    action: 'claim-redeem',
    amount: '0',
    vault,
    all: true,
    ignoreAmount: true,
  })

  const { executeTx, currentActionIndex } = useTransaction(network.name, actions, '0', setTransactionFinished)

  const [isPending, setIsPending] = useState(false)

  const onClaim = async () => {
    if (!account) {
      setOpenWalletModal('connect')
      return
    }
    setIsPending(true)
    try {
      await executeTx()
    } finally {
      setIsPending(false)
    }
  }

  const isClaimDataEmpty = claimData.length === 0
  const isClaimable = claimData.some((data) => data.status === ClaimStatus.READY)
  const isClaimDisabled = isClaimDataEmpty || !isClaimable
  const isSubmitLoading = isFetching || txState === 'loading' || isPending

  return (
    <TabsContent value={stepData.name} className="mt-3 flex flex-col gap-3 md:mt-4 md:gap-4">
      {isClaimDataEmpty ? (
        <p className="text-xs">No claimable withdrawals</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow variant="header">
              <TableHead>Request</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {claimData.map((data) => (
              <TableRow key={data.id}>
                <TableCell>#{data.id}</TableCell>
                <TableCell>
                  {formatPreciseTokenValue({
                    value: data.assetAmount,
                    decimals: asset.decimals,
                  })}{' '}
                  {asset.symbol}
                </TableCell>
                <TableCell>
                  <Badge color={data.status === 'pending' ? 'orange' : 'green'}>{data.status}</Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Note>
        <p>
          According to{' '}
          {protocol.protocolUrl ? (
            <Button variant="default" size="default" className="rounded-full underline" asChild>
              <a href={protocol.protocolUrl} target="_blank" rel="noreferrer">
                <span className="inline-block">{protocolDisplayName}</span>
              </a>
            </Button>
          ) : (
            <span className="inline-block">{protocolDisplayName}</span>
          )}
          , withdrawals can take up to ~6.5 days to process.
        </p>
      </Note>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          onClaim()
        }}
        className="flex flex-col gap-3"
      >
        <TxSteps
          account={account}
          stepData={stepData}
          activeStep={currentActionIndex}
          disabled={isClaimDisabled}
          loading={isSubmitLoading}
          label="Claim withdrawal"
          network={network.name}
          success={txState === 'success'}
        />
      </form>
    </TabsContent>
  )
}
